// Relatórios fiscais e exportações (XML do período em .zip, CSV).
import React, { useState } from "react";
import { abrirArquivo, fiscal } from "../api";
import { Aviso, Botao, Campo, Cartao, Entrada, Selecao, Tabela } from "../components/ui";
import { brl, hojeISO, inicioMesISO, num } from "../utils";

const MOEDA = new Set(["valor_total", "valor_nota", "valor_produtos", "valor_icms", "valor_unitario", "desconto",
  "icms", "difal", "nfe_valor", "nfce_valor", "nfse_valor", "total", "base_iss", "valor_iss", "iss_retido",
  "total_retencoes", "valor_liquido"]);

const RELATORIOS = [
  ["nfe-por-itens", "Notas Fiscais NF-e por itens"],
  ["nfe", "Notas Fiscais NF-e"],
  ["nfe-por-cfop", "Notas Fiscais NF-e por CFOP"],
  ["nfe-por-uf", "Notas Fiscais NF-e por UF"],
  ["nfse-por-uf", "Notas Fiscais de Serviço NFS-e por UF"],
  ["faturamento", "Faturamento por período"],
  ["faturamento-por-cliente", "Faturamento por período agrupado por cliente"],
];

const rotulo = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export default function Relatorios() {
  const [rel, setRel] = useState("nfe");
  const [de, setDe] = useState(inicioMesISO());
  const [ate, setAte] = useState(hojeISO());
  const [modelo, setModelo] = useState("55");
  const [agrup, setAgrup] = useState("dia");
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const usaModelo = rel.startsWith("nfe");
  const params = { de, ate, ...(usaModelo ? { modelo } : {}), ...(rel === "faturamento" ? { agrupamento: agrup } : {}) };

  const gerar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDados(await fiscal.relatorio(rel, params));
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };

  const colunas = dados?.linhas?.length
    ? Object.keys(dados.linhas[0]).map((k) => ({
        id: k,
        rotulo: rotulo(k),
        direita: typeof dados.linhas[0][k] === "number",
        render: (l) => (MOEDA.has(k) ? brl(l[k]) : typeof l[k] === "number" && !Number.isInteger(l[k]) ? num(l[k], 4) : l[k]),
      }))
    : [{ id: "vazio", rotulo: "" }];

  const totais = dados?.linhas?.length
    ? Object.keys(dados.linhas[0]).filter((k) => MOEDA.has(k) && k !== "valor_unitario")
        .map((k) => [k, dados.linhas.reduce((a, l) => a + Number(l[k] || 0), 0)])
    : [];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Relatórios fiscais</h1>
      <Cartao>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <Campo rotulo="Relatório" className="col-span-2"><Selecao value={rel} onChange={(e) => { setRel(e.target.value); setDados(null); }} opcoes={RELATORIOS} /></Campo>
          <Campo rotulo="De"><Entrada type="date" value={de} onChange={(e) => setDe(e.target.value)} /></Campo>
          <Campo rotulo="Até"><Entrada type="date" value={ate} onChange={(e) => setAte(e.target.value)} /></Campo>
          {usaModelo && <Campo rotulo="Tipo"><Selecao value={modelo} onChange={(e) => setModelo(e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"]]} /></Campo>}
          {rel === "faturamento" && <Campo rotulo="Agrupar"><Selecao value={agrup} onChange={(e) => setAgrup(e.target.value)} opcoes={[["dia", "Por dia"], ["mes", "Por mês"]]} /></Campo>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Botao carregando={carregando} onClick={gerar}>Gerar</Botao>
          <Botao variante="secundario" onClick={() => abrirArquivo(`/relatorios/${rel}`, { params: { ...params, formato: "csv" }, baixar: true, nome: `${rel}-${de}-${ate}.csv` })}>
            Baixar CSV (Excel)
          </Botao>
        </div>
      </Cartao>

      {rel === "nfse-por-uf" && <Aviso>NFS-e autorizadas no período, agrupadas pela UF do tomador. Notas sem tomador identificado aparecem como “Não identificado”.</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {dados && (
        <Cartao titulo={`${RELATORIOS.find(([k]) => k === rel)[1]} — ${dados.total_linhas} linha(s)`}>
          <Tabela colunas={colunas} linhas={dados.linhas} vazio="Sem dados no período" />
          {totais.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-end gap-6 border-t pt-3 text-sm border-gray-200">
              {totais.map(([k, v]) => <span key={k}>{rotulo(k)}: <strong>{brl(v)}</strong></span>)}
            </div>
          )}
        </Cartao>
      )}

      <ExportarXml />
    </div>
  );
}

function ExportarXml() {
  const [de, setDe] = useState(inicioMesISO());
  const [ate, setAte] = useState(hojeISO());
  const [modelo, setModelo] = useState("");
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState(null);
  const baixar = async () => {
    setBaixando(true);
    setErro(null);
    try {
      await abrirArquivo("/exportacao/xml", { params: { de, ate, modelo }, baixar: true, nome: `xml-${de}-${ate}.zip` });
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(false);
    }
  };
  return (
    <Cartao titulo="Exportação de XML (para o contador)">
      <div className="flex flex-wrap items-end gap-3">
        <Campo rotulo="De"><Entrada type="date" value={de} onChange={(e) => setDe(e.target.value)} /></Campo>
        <Campo rotulo="Até"><Entrada type="date" value={ate} onChange={(e) => setAte(e.target.value)} /></Campo>
        <Campo rotulo="Tipo" className="w-36"><Selecao value={modelo} vazio="NF-e e NFC-e" onChange={(e) => setModelo(e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"]]} /></Campo>
        <Botao carregando={baixando} onClick={baixar}>Baixar .zip</Botao>
      </div>
      <p className="mt-2 text-xs text-gray-500">Inclui notas autorizadas e canceladas, separadas em pastas NFe/NFCe.</p>
      {erro && <div className="mt-2"><Aviso tipo="erro">{erro}</Aviso></div>}
    </Cartao>
  );
}
